import cuid from "cuid";
import { get, isString, keyBy, map, max } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { BUILD_GENERAL_COST } from "utils/api/graphql/mutations/costs";
import { INSURANCE_PLAN } from "utils/api/graphql/queries/insurance-plans";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
  PROJECT_TYPES,
  SALE_MODES,
} from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

function findRules({ params, ruleName }) {
  let results = [];

  params.forEach((plan) => {
    if (plan.rules && Array.isArray(plan.rules)) {
      plan.rules.forEach((rule) => {
        if (rule.name === ruleName) {
          results.push(rule.value);
        }
      });
    }
  });

  return results;
}

const EffectiveDate = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
  updateUrl,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.EFFECTIVE_DATE", { returnObjects: true });
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const insurancePlanId = useMemo(() => {
    return (
      get(
        get(project, "subscriptions", []).find(
          ({ id }) => id === subscriptionId,
        ),
        "insurancePlan.id",
      ) || get(project, "fields.lastSelected.0")
    );
  }, []);

  const DEFAULT_DELTA_START = 1;
  const DEFAULT_DELTA_START_RIA = 40;
  const DEFAULT_DELTA_END_RIA = 0;
  const DEFAULT_DELTA_END = 0;
  const [loading, setLoading] = useState(false);
  const [deltas, setDeltas] = useState({
    start: { normal: DEFAULT_DELTA_START, ria: DEFAULT_DELTA_START_RIA },
    end: { normal: DEFAULT_DELTA_END, ria: DEFAULT_DELTA_END_RIA },
  });
  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT);
  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST, {
    variables: {
      data: {
        project: { id: project.id },
        insurancePlan: {
          id: get(data, "subscription.insurancePlan.id"),
        },
      },
    },
  });

  const [getGeneralCost, { data: generalCost, refetch }] = useLazyQuery(
    graphql.queries.GENERAL_COST,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          project: { id: get(project, "id") },
          insurancePlan: {
            id: get(data, "subscription.insurancePlan.id"),
          },
        },
      },
    },
  );

  useEffect(() => {
    if (get(data, "subscription.insurancePlan.id")) {
      getGeneralCost();
    }
  }, [data]);

  useQuery(INSURANCE_PLAN, {
    variables: {
      where: {
        id: insurancePlanId,
      },
    },
    onCompleted: ({ insurancePlan }) => {
      const deltaStartRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          }),
        ) || DEFAULT_DELTA_START_RIA;
      const deltaStart =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          }),
        ) || DEFAULT_DELTA_START;
      const deltaEnd =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          }),
        ) || DEFAULT_DELTA_END;
      const deltaEndRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          }),
        ) || DEFAULT_DELTA_END_RIA;
      setDeltas({
        start: { normal: deltaStart, ria: deltaStartRia },
        end: { normal: deltaEnd, ria: deltaEndRia },
      });
    },
  });

  const recalculateCost = ({
    issuanceDate,
    oldIssuanceDate,
    setFieldValue,
    setShowChangeCostModal,
    isCancel,
    isRia,
  }) => {
    const cost = get(generalCost, "generalCost.cost");
    const formattedIssuanceDate = isString(issuanceDate)
      ? moment(issuanceDate)
      : issuanceDate;

    const formattedOldIssuanceDate = isString(oldIssuanceDate)
      ? moment(oldIssuanceDate)
      : oldIssuanceDate;

    if (formattedIssuanceDate.isSame(formattedOldIssuanceDate.format(), "day"))
      return;

    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contract: {
            id: get(project, "contract.id"),
            issuanceDate,
          },
        },
      },
      onCompleted: () => {
        buildGeneralCost({
          onCompleted: ({ buildGeneralCost: generalCost }) => {
            if (!generalCost?.cost) {
              setFieldValue("contract.issuanceDate", oldIssuanceDate);
              setShowChangeCostModal({
                noCost: true,
              });
              return updateProject({
                variables: {
                  where: { id: project.id },
                  data: {
                    contract: {
                      id: get(project, "contract.id"),
                      issuanceDate: oldIssuanceDate,
                    },
                  },
                },
                onCompleted: () => {
                  buildGeneralCost();
                },
              });
            }
            if (cost === generalCost.cost) return;
            if (isCancel) {
              setFieldValue("contract.issuanceDate", issuanceDate);
              refetch();
              return setShowChangeCostModal({});
            }
            setShowChangeCostModal({
              isRia,
              issuanceDate: {
                old: formattedOldIssuanceDate.format(DATE_FORMAT),
                new: formattedIssuanceDate.format(DATE_FORMAT),
              },
              cost: {
                old: cost,
                new: generalCost.cost,
              },
            });
            refetch();
          },
        });
      },
    });
  };

  const onSubmit = ({ contract, ria, fields, terminationRequest }) => {
    // setLoading(true);
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    const profession = get(fields, "profession");
    const familyStatus = get(fields, "familyStatus");
    const isPPE = get(fields, "isPPE");
    const ppeFunction = get(fields, "ppeFunction");
    const ppeRelated = get(fields, "ppeRelated");
    const birthCountry = get(fields, "birthCountry");
    const birthDepartment = get(fields, "birthDepartment");

    // Prepare contact fields (excluding isPPE which goes to contact.politicallyExposedPerson)
    const contactFields = {
      ...get(project, "contact.fields", {}),
      ...(ppeFunction && { ppeFunction }),
      ...(ppeRelated !== undefined && { ppeRelated }),
    };

    // Prepare telecoms with birthCountry and birthDepartment
    const existingTelecoms = keyBy(
      get(project, "contact.telecoms", []),
      "system",
    );
    const updatedTelecoms = { ...existingTelecoms };

    if (birthCountry) {
      updatedTelecoms[CONTACT_POINT_SYSTEM.BIRTH_COUNTRY] = {
        id: existingTelecoms[CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]?.id || cuid(),
        system: CONTACT_POINT_SYSTEM.BIRTH_COUNTRY,
        value: birthCountry,
      };
    }

    if (birthDepartment) {
      updatedTelecoms[CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH] = {
        id:
          existingTelecoms[CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]?.id ||
          cuid(),
        system: CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH,
        value: birthDepartment,
      };
    }

    const telecomsArray = map(updatedTelecoms, (item) => item);

    // Omit relatedPersons: re-saving them from project state can wipe
    // identity.socialRegime when the client cache is incomplete.
    const { relatedPersons: _relatedPersons, ...contactWithoutRelatedPersons } =
      get(project, "contact") || {};

    return onNext({
      payload: {
        contact: {
          ...contactWithoutRelatedPersons,
          ...(profession && { profession }),
          ...(familyStatus && { familySituation: familyStatus }),
          ...(isPPE !== undefined && { politicallyExposedPerson: isPPE }),
          ...(Object.keys(contactFields).length > 0 && {
            fields: contactFields,
          }),
          ...(telecomsArray.length > 0 && { telecoms: telecomsArray }),
          acceptContact: true,
          acceptDigital: true,
        },
        contract: {
          ...contract,
          caseType: ria
            ? CASE_TYPES.RETURN_TO_COMPETITION
            : CASE_TYPES.NEW_CONTRACT,
          saleMode: SALE_MODES.DISTANT,
        },
        ria,
        fields,
        terminationRequest,
        projectType: PROJECT_TYPES.HOT_LEAD,
      },
      onCompleted: () => setLoading(false),
    });
  };

  if (loading || !get(data, "subscription")) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showProfile
        showCart
      />
      <View
        contract={get(project, "contract", {})}
        fields={get(project, "fields", {})}
        terminationRequest={get(project, "terminationRequest") || null}
        ria={project?.ria}
        locked={get(data, "subscription.locked")}
        deltas={deltas}
        onSubmit={onSubmit}
        onBack={onBack}
        isCollective={!!get(project, "fields.branch")}
        translate={TRANSLATION}
        recalculateCost={recalculateCost}
        subscription={get(data, "subscription")}
        project={project}
      />
    </>
  );
};

export default EffectiveDate;
